import React from 'react';
import { Bar } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip as ChartTooltip, Legend, ChartData, ChartOptions } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { Tooltip, Typography } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info'; // Import an info icon
import { PowerDieColor, SimulationLoadoutStatsOutput } from '../core/types';
import './Histogram.css'; // Import the CSS file
import '../styles.css';

const FALLBACK_CHART_TEXT_COLOR = '#f8f5ef';

function readRootCssValue(propertyName: string): string {
    if (typeof window === 'undefined' || typeof window.getComputedStyle !== 'function') {
        return '';
    }

    return window.getComputedStyle(document.documentElement)?.getPropertyValue?.(propertyName)?.trim?.() ?? '';
}

function getChartTextColor(): string {
    return readRootCssValue('--primary-text-color') || readRootCssValue('--accent-text-color') || FALLBACK_CHART_TEXT_COLOR;
}

// Register Chart.js components and plugins
ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    ChartTooltip,
    Legend,
    ChartDataLabels, // Register the datalabels plugin
);

interface HistogramProps {
    data: SimulationLoadoutStatsOutput[];
    aeonTrespassValue: number;
    aeonTrespassValues?: number[];
    isLoading?: boolean;
    placeholderTitles?: string[];
}

// Array of colors for different graphs
const colors = [
    'rgba(75, 192, 192, 0.6)', // Teal
    'rgba(255, 99, 132, 0.6)', // Red
    'rgba(54, 162, 235, 0.6)', // Blue
    'rgba(255, 206, 86, 0.6)', // Yellow
    'rgba(153, 102, 255, 0.6)', // Purple
    'rgba(255, 159, 64, 0.6)', // Orange
    'rgba(99, 255, 132, 0.6)', // Light Green
    'rgba(199, 99, 255, 0.6)', // Violet
    'rgba(235, 64, 52, 0.6)', // Bright Red
    'rgba(64, 235, 216, 0.6)', // Cyan
];

// Recursive function to filter out properties that are null, zero, empty arrays, or empty objects
const filterObject = (obj: any): any => {
    if (Array.isArray(obj)) {
        return obj.map((item) => filterObject(item)).filter((item) => item !== null && item !== undefined && (Array.isArray(item) ? item.length !== 0 : Object.keys(item).length !== 0));
    } else if (typeof obj === 'object' && obj !== null) {
        const filteredObj = Object.entries(obj).reduce((acc, [key, value]) => {
            const filteredValue = filterObject(value);
            if (
                filteredValue !== null &&
                filteredValue !== 0 &&
                filteredValue !== '' &&
                !(Array.isArray(filteredValue) && filteredValue.length === 0) &&
                !(typeof filteredValue === 'object' && Object.keys(filteredValue).length === 0)
            ) {
                acc[key] = filteredValue;
            }
            return acc;
        }, {} as any);

        return Object.keys(filteredObj).length > 0 ? filteredObj : undefined;
    }

    return obj;
};

function simplifyDiceOutput(diceArray: PowerDieColor[]): string {
    const diceCounts: { [key in PowerDieColor]?: number } = diceArray.reduce(
        (acc, die) => {
            acc[die] = (acc[die] || 0) + 1;
            return acc;
        },
        {} as { [key in PowerDieColor]?: number },
    );

    const orderedColors: PowerDieColor[] = [PowerDieColor.RED, PowerDieColor.BLACK, PowerDieColor.WHITE, PowerDieColor.MORTAL];

    return orderedColors
        .filter((color) => diceCounts[color]) // Only include colors that exist
        .map((color) => `${diceCounts[color]} ${color}`)
        .join(', ');
}

// Function to format loadout information for the tooltip (filtered with nested objects)
const formatLoadoutInfo = (loadout: any): string => {
    const filteredLoadout = filterObject(loadout);
    return 'LOADOUT SUMMARY\n' + JSON.stringify(filteredLoadout, null, 2); // Format with 2 spaces for indentation
};

const Histogram: React.FC<HistogramProps> = ({ data, aeonTrespassValue, aeonTrespassValues, isLoading = false, placeholderTitles = [] }) => {
    const chartTextColor = getChartTextColor();
    const hasPlaceholderState = data.length === 0 && placeholderTitles.length > 0;
    // Determine the global range of power values
    const allPowers = data.flatMap((loadout) => loadout.power);
    const maxPower = Math.max(5, ...allPowers); // Ensure the maximum power is at least 5
    const minATValue = aeonTrespassValues?.length ? aeonTrespassValues[0] : aeonTrespassValue;
    const maxATValue = aeonTrespassValues?.length ? aeonTrespassValues[aeonTrespassValues.length - 1] : aeonTrespassValue;

    // Function to create chart data for the cumulative probability of each power value
    const createChartData = (cumulativeProbabilities: number[], color: string): ChartData<'bar'> => {
        const labels = Array.from(
            { length: maxPower },
            (_, i) => i + 1, // Start from 1 (excluding 0 from display)
        );

        // Adjust background color based on Aeon Trespass Value
        const backgroundColors = labels.map((label) => {
            if (label >= maxATValue) {
                return color;
            }
            if (label >= minATValue) {
                return color.replace('0.6', '0.4');
            }
            return color.replace('0.6', '0.2');
        });

        return {
            labels,
            datasets: [
                {
                    label: 'Power Probability (%)',
                    data: cumulativeProbabilities.slice(1, cumulativeProbabilities.length),
                    backgroundColor: backgroundColors,
                    borderColor: color.replace('0.6', '1'),
                    borderWidth: 1,
                },
            ],
        };
    };

    // Chart options with Y-axis ranging from 0 to 105 to provide space for labels and X-axis showing only whole numbers
    const chartOptions: ChartOptions<'bar'> = {
        responsive: true,
        maintainAspectRatio: false,
        layout: {
            padding: {
                top: 25,
                bottom: 8,
            },
        },
        scales: {
            x: {
                type: 'linear',
                min: 1,
                max: maxPower,
                title: {
                    display: true,
                    text: 'Power',
                    color: chartTextColor,
                    padding: {
                        top: 4,
                    },
                },
                ticks: {
                    color: chartTextColor,
                    stepSize: 1,
                    callback: function (tickValue: string | number) {
                        if (typeof tickValue === 'number' && Number.isInteger(tickValue)) {
                            return tickValue;
                        }
                        return null;
                    },
                },
            },
            y: {
                min: 0,
                max: 100,
                ticks: {
                    color: chartTextColor,
                    callback: (value: number | string) => `${value}%`,
                    stepSize: 10,
                },
            },
        },
        plugins: {
            legend: {
                display: false,
            },
            datalabels: {
                anchor: 'end',
                align: 'end',
                formatter: function (value, context) {
                    const dataset = context.dataset.data;
                    const index = context.dataIndex;

                    // If the current value is the same as the previous one, skip the label
                    if (index > 0 && dataset[index] === dataset[index - 1]) {
                        return ''; // Return an empty string to hide the label
                    }

                    // Otherwise, display the value
                    return `${value.toFixed(0)}%`;
                },
                color: chartTextColor,
                font: {
                    weight: 'bold',
                    size: 12,
                },
            },
        },
    };

    const resolvedPlaceholderTitles = placeholderTitles.length > 0 ? placeholderTitles : ['Loadout 1'];

    return (
        <div className="histogram-container">
            {hasPlaceholderState &&
                resolvedPlaceholderTitles.map((title, index) => (
                    <div key={`placeholder-${index}`} className="histogram-section histogram-section-loading">
                        <div className="histogram-title histogram-title-static">{title}</div>
                        <div className="histogram-chance histogram-placeholder-line histogram-placeholder-line-wide" />
                        <div className="histogram-details histogram-placeholder-line histogram-placeholder-line-medium" />
                        <div className="histogram-details histogram-placeholder-line histogram-placeholder-line-short" />
                        <div className="histogram-placeholder-chart" aria-hidden="true">
                            <div className="histogram-placeholder-bars">
                                {[44, 62, 78, 55, 88, 70].map((height, barIndex) => (
                                    <span key={barIndex} className="histogram-placeholder-bar" style={{ height: `${height}%` }} />
                                ))}
                            </div>
                        </div>
                        <div className="histogram-loading-mask" aria-hidden="true">
                            <div className="histogram-loading-spinner" />
                        </div>
                    </div>
                ))}

            {data.map((simOutput, index) => {
                const cumulativeProbabilities = simOutput.powerCumulativeProbabilities;
                const displayTitle = simOutput.loadout.name?.trim() || `Loadout ${index + 1}`;
                return (
                    <div key={index} className={`histogram-section ${isLoading ? 'histogram-section-loading' : ''}`}>
                        <Tooltip title={<pre className="tooltip-pre">{formatLoadoutInfo(simOutput.loadout)}</pre>} arrow enterDelay={500} enterNextDelay={500}>
                            <Typography variant="h6" className="histogram-title">
                                {displayTitle}
                                <InfoIcon className="info-icon" />
                            </Typography>
                        </Tooltip>

                        <Typography variant="body1" className="histogram-chance">
                            Chance to Wound = {simOutput.chanceToWound}%
                        </Typography>
                        <Typography variant="body1" className="histogram-details">
                            Full Miss = {simOutput.chanceToFullMiss}% | Hit = {simOutput.chanceToHit}% | Full Hit = {simOutput.chanceToFullHit}%
                            <br />
                            Full Hit Power Dice = {simplifyDiceOutput(simOutput.diceRolledOnFullHit)}
                        </Typography>

                        <div className="histogram-chart-canvas">
                            <Bar data={createChartData(cumulativeProbabilities, colors[index % colors.length])} options={chartOptions} />
                        </div>
                        {isLoading && (
                            <div className="histogram-loading-mask" aria-hidden="true">
                                <div className="histogram-loading-spinner" />
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
};

export default Histogram;
